if (customElements && !customElements.get('additional-product-widget')) {
  class AdditionalProductWidget extends HTMLElement {
    constructor() {
      super();

      this.variables = {
        currentProductVariantHolder: document.querySelector('[id^=variant-selects-template-]') || null,
        availableGifts: this.querySelectorAll('.js-gift-product-selector input'),
        productForm: document.querySelector('form[action*="/cart/add"]'),
        dataAvailableGifts: []
      };
    }

    connectedCallback() {
      if (!this.variables.currentProductVariantHolder) return;

      this.initDataAvailableGifts();
      this.setGift();

      this.bindEvents();
    }

    get currentProductVariant() {
      const {currentProductVariantHolder} = this.variables;
      if (!currentProductVariantHolder) return null;

      return currentProductVariantHolder.querySelector('input:checked') || null;
    }

    get currentProductVariantTitle() {
      // Try to get variant title from variant-selects element
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects && variantSelects.currentVariant) {
        return variantSelects.currentVariant.title || '';
      }

      // Fallback: try to get from the checked input's label or parent
      const checkedInput = this.currentProductVariant;
      if (checkedInput) {
        // Try to find label associated with the input
        const label = checkedInput.closest('label');
        if (label) {
          const labelText = label.textContent.trim();
          if (labelText) {
            return labelText;
          }
        }
        
        // Try to get from data attribute if available
        if (checkedInput.dataset.variantTitle) {
          return checkedInput.dataset.variantTitle;
        }
      }

      // Last resort: try to get from product JSON data
      const variantId = Number(checkedInput?.value);
      if (variantId && variantSelects) {
        const variantData = variantSelects.getVariantData?.();
        if (variantData) {
          const variant = variantData.find(v => v.id === variantId);
          if (variant) {
            return variant.title || '';
          }
        }
      }

      return '';
    }

    extractSizeFromVariantTitle(variantTitle) {
      if (!variantTitle) return null;

      // First try to extract size range (e.g., "34-37")
      // Examples: "XS (Maat 34-37) / 1-pack" or "34-37" or "S (Maat 38-41) / 1-pack"
      const sizeRangeMatch = variantTitle.match(/(\d+)-(\d+)/);
      if (sizeRangeMatch) {
        const minSize = Number(sizeRangeMatch[1]);
        const maxSize = Number(sizeRangeMatch[2]);
        
        return {
          min: minSize,
          max: maxSize,
          range: [minSize, maxSize],
          isRange: true
        };
      }

      // If no range found, try to extract single size (e.g., "44")
      // Examples: "44, Enkel werkschoen" or just "44"
      const singleSizeMatch = variantTitle.match(/\b(\d+)\b/);
      if (singleSizeMatch) {
        const size = Number(singleSizeMatch[1]);
        
        return {
          min: size,
          max: size,
          range: [size, size],
          isRange: false
        };
      }

      return null;
    }

    get currentGiftProductId() {
      return this.dataset.currentGiftProductId || null;
    }

    set currentGiftProductId(value) {
      this.dataset.currentGiftProductId = value;
    }

    initDataAvailableGifts() {
      const {availableGifts, dataAvailableGifts} = this.variables;

      availableGifts.forEach((giftInput) => {
        const variantTitle = giftInput.dataset.variantsName;
        
        // Filter to only include "1-pack" variants
        if (!variantTitle.toLowerCase().includes('1-pack')) {
          return;
        }

        // Extract size range from variant title (e.g., "34-37" from "XS (Maat 34-37) / 1-pack" or just "34-37")
        const sizeRangeMatch = variantTitle.match(/(\d+)-(\d+)/);
        if (!sizeRangeMatch) {
          return;
        }

        const valueGiftVariantMinMax = [Number(sizeRangeMatch[1]), Number(sizeRangeMatch[2])];
        const giftProductId = giftInput.value;

        const availableSizes = [];

        for (let i = valueGiftVariantMinMax[0]; i <= valueGiftVariantMinMax[1]; i++) {
          availableSizes.push(i);
        }

        dataAvailableGifts.push({
          element: giftInput,
          availableSizes,
          giftProductId,
          variantTitle
        });
      });
    }

    async clearGiftsFromCart() {
      const {dataAvailableGifts} = this.variables;

      const productData = {};

      dataAvailableGifts.forEach((gift) => {
        productData[gift.giftProductId] = 0;
      })

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates: productData
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }

    setGift() {
      const {dataAvailableGifts} = this.variables;
      
      if (dataAvailableGifts.length === 0) {
        console.warn('No 1-pack gift variants available');
        return;
      }

      // Debug: log all available gift variants
      console.log('Available 1-pack gift variants:', dataAvailableGifts.map(g => ({
        title: g.variantTitle,
        size: this.extractSizeFromVariantTitle(g.variantTitle)
      })));

      // Get the main product variant title
      const mainProductVariantTitle = this.currentProductVariantTitle;
      console.log('Main product variant title:', mainProductVariantTitle);
      
      // Extract size from main product variant title
      const mainProductSize = this.extractSizeFromVariantTitle(mainProductVariantTitle);
      console.log('Main product size:', mainProductSize);
      
      let dataGift = null;

      if (mainProductSize) {
        // Find gift variant that matches the size
        dataGift = dataAvailableGifts.find(item => {
          const giftSize = this.extractSizeFromVariantTitle(item.variantTitle);
          if (!giftSize) return false;
          
          // If main product is a single size, check if it falls within the gift variant's range
          if (!mainProductSize.isRange) {
            // Single size: check if it's within the gift variant's range
            return mainProductSize.min >= giftSize.min && mainProductSize.min <= giftSize.max;
          }
          
          // If main product is a range, check if it overlaps or is contained within gift variant's range
          // Main product size should be within gift variant size range
          return mainProductSize.min >= giftSize.min && mainProductSize.max <= giftSize.max;
        });

        // If no exact match, try to find the closest match by checking if any size in the range matches
        if (!dataGift) {
          dataGift = dataAvailableGifts.find(item => {
            const giftSize = this.extractSizeFromVariantTitle(item.variantTitle);
            if (!giftSize) return false;
            
            // Check if there's any overlap in the size ranges
            if (!mainProductSize.isRange) {
              // Single size: check if it's within the gift variant's range
              return mainProductSize.min >= giftSize.min && mainProductSize.min <= giftSize.max;
            }
            
            // Range overlap check
            return (mainProductSize.min <= giftSize.max && mainProductSize.max >= giftSize.min);
          });
        }
      }

      // Fallback: if no size match found, use the first available gift
      if (!dataGift) {
        console.warn('No matching gift variant found for size, using first available');
        dataGift = dataAvailableGifts[0];
      }

      if (dataGift && dataGift.element) {
        console.log('Selected gift variant:', dataGift.variantTitle, 'for main product size:', mainProductSize);
        dataGift.element.click();
        this.currentGiftProductId = dataGift.giftProductId;
      }
    }

    handleSetGift() {
      const {currentProductVariantHolder} = this.variables;
      
      // Listen for changes in the variant-selects-template element
      if (currentProductVariantHolder) {
        currentProductVariantHolder.addEventListener('change', (event) => {
          if (event.target.type === 'radio') {
            // Small delay to ensure variant-selects has updated
            setTimeout(() => {
              this.setGift();
            }, 50);
          }
        });
      }

      // Also listen for variant changes from variant-selects element
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        // Listen for the custom product:variant-change event
        document.addEventListener('product:variant-change', () => {
          setTimeout(() => {
            this.setGift();
          }, 50);
        });
      }
    }

    updateCartDrawer() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    async addToCartGift(productId, quantity = 1) {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [productId]: quantity
            }
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }

    handleAddToCartGift() {
      const {productForm} = this.variables;
      if (!productForm) return;

      productForm.addEventListener('submit',  (event) => {
        if (!this.currentGiftProductId) return;

        setTimeout( async () => {
          await this.clearGiftsFromCart();
          await this.clearGiftsFromCart();

          await this.addToCartGift(this.currentGiftProductId);

          this.updateCartDrawer();
        }, 500)


      });
    }

    bindEvents() {
      this.handleSetGift();
      this.handleAddToCartGift();
    }
  }

  customElements.define('additional-product-widget', AdditionalProductWidget);
}
